import {
  DataGrid,
  FilterToolbar,
  formatDate,
  StatusBadge,
  useNotifications,
  type FilterDef,
  type FilterValues,
  type GridQuery,
  type Severity
} from '@ijoin/components';
import {
  getGetAdminAutoForceOutsQueryKey,
  useGetAdminAutoForceOuts,
  useGetAdminPlanOptions,
  usePostAdminAutoForceOutCancel,
  usePostAdminAutoForceOutReprocess,
  type AdminAutoForceOutRowRes,
  type GetAdminAutoForceOutsParams
} from '@ijoin/hooks';
import { Stack, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { useState } from 'react';

import { AutoForceOutActionsCell } from '../../components';
import { useAdvisoryOrganizationOptions } from '../../components/useAdvisoryOrganizationOptions';
import { ApiError } from '../../types';

const STATUS_SEVERITY: Record<string, Severity> = {
  canceled: 'error',
  liquidated: 'success',
  liquidationRequested: 'info',
  onHold: 'warning',
  pending: 'warning',
  stalled: 'error'
};

const buildMutationOptions = (params: {
  notifications: ReturnType<typeof useNotifications>;
  queryClient: ReturnType<typeof useQueryClient>;
  success: string;
}) => ({
  onError: (error: ApiError) => params.notifications.error(error.message),
  onSuccess: () => {
    params.queryClient.invalidateQueries({
      queryKey: getGetAdminAutoForceOutsQueryKey()
    });
    params.notifications.success(params.success);
  }
});

export const Page = () => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();

  const [query, setQuery] = useState<GridQuery>();
  const [draftOrgId, setDraftOrgId] = useState<null | string>();

  const candidates = useGetAdminAutoForceOuts(
    query as GetAdminAutoForceOutsParams,
    { query: { enabled: !!query, placeholderData: keepPreviousData } }
  );

  const orgs = useAdvisoryOrganizationOptions();

  const appliedOrgId = (query as GetAdminAutoForceOutsParams | undefined)
    ?.advisoryOrganizationId;
  const plansOrgId =
    draftOrgId === undefined ? appliedOrgId : (draftOrgId ?? undefined);

  const plans = useGetAdminPlanOptions(
    { ...(plansOrgId && { advisoryOrganizationId: plansOrgId }) },
    {
      query: {
        select: res =>
          res.map(plan => ({
            label: plan.description ?? plan.planId,
            value: plan.planId
          }))
      }
    }
  );

  const reprocess = usePostAdminAutoForceOutReprocess<ApiError>({
    mutation: buildMutationOptions({
      notifications,
      queryClient,
      success: 'Force out candidate queued for reprocessing'
    })
  });

  const cancel = usePostAdminAutoForceOutCancel<ApiError>({
    mutation: buildMutationOptions({
      notifications,
      queryClient,
      success: 'Force out candidate canceled'
    })
  });

  const onDraftOrgChange = (values: FilterValues) =>
    setDraftOrgId((values.advisoryOrganizationId as string) || null);

  const columns: GridColDef<AdminAutoForceOutRowRes>[] = [
    {
      field: 'participant',
      flex: 1,
      headerName: 'Participant',
      minWidth: 180,
      renderCell: params =>
        `${params.row.firstName ?? ''} ${params.row.lastName ?? ''}`.trim(),
      sortable: false
    },
    {
      field: 'planDescription',
      flex: 1.4,
      headerName: 'Plan',
      minWidth: 200
    },
    {
      field: 'distributionStatus',
      flex: 1,
      headerName: 'Status',
      minWidth: 190,
      renderCell: params => (
        <StatusBadge
          data-testid={`status-${params.row.rolloverParticipantId}`}
          severity={STATUS_SEVERITY[params.value ?? ''] ?? 'info'}>
          {params.row.distributionStatusLabel ?? ''}
        </StatusBadge>
      )
    },
    {
      align: 'right',
      field: 'attemptCount',
      headerAlign: 'right',
      headerName: 'Failed Attempts',
      width: 100
    },
    {
      field: 'lastResponse',
      flex: 2,
      headerName: 'Last Response',
      minWidth: 260,
      renderCell: params => (
        <Tooltip title={params.value ?? ''}>
          <Typography
            data-testid={`lastResponse-${params.row.rolloverParticipantId}`}
            noWrap
            sx={{ lineHeight: 'inherit' }}
            variant='body2'>
            {params.value ?? ''}
          </Typography>
        </Tooltip>
      ),
      sortable: false
    },
    {
      field: 'initialAuditOn',
      headerName: 'Identified',
      renderCell: params => formatDate(params.value),
      width: 150
    },
    {
      field: 'sentToProviderDate',
      headerName: 'Sent To Provider',
      renderCell: params => formatDate(params.value),
      width: 150
    },
    {
      field: 'responseDeadline',
      headerName: 'Response Deadline',
      renderCell: params => formatDate(params.value),
      width: 160
    },
    {
      field: 'liquidationRequestDate',
      headerName: 'Liquidation Requested',
      renderCell: params => formatDate(params.value),
      width: 175
    },
    {
      field: 'liquidationDate',
      headerName: 'Liquidated',
      renderCell: params => formatDate(params.value),
      width: 150
    },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <AutoForceOutActionsCell
          distributionStatus={params.row.distributionStatus}
          onCancel={() =>
            cancel.mutate({
              rolloverParticipantId: params.row.rolloverParticipantId
            })
          }
          onReprocess={() =>
            reprocess.mutate({
              rolloverParticipantId: params.row.rolloverParticipantId
            })
          }
          participantName={`${params.row.firstName ?? ''} ${params.row.lastName ?? ''}`.trim()}
          rolloverParticipantId={params.row.rolloverParticipantId}
        />
      ),
      sortable: false,
      width: 70
    }
  ];

  const filterDefs: FilterDef[] = [
    { label: 'First Name', name: 'firstName', type: 'text' },
    { label: 'Last Name', name: 'lastName', type: 'text' },
    {
      isLoading: orgs.isFetching,
      label: 'Advisory Organization',
      name: 'advisoryOrganizationId',
      options: orgs.data ?? [],
      type: 'autocomplete'
    },
    {
      column: 'planDescription',
      isLoading: plans.isFetching,
      label: 'Plan',
      name: 'planId',
      options: plans.data ?? [],
      type: 'autocomplete'
    },
    {
      column: 'distributionStatus',
      label: 'Status',
      name: 'distributionStatus',
      options: [
        { label: 'Pending, Identified response', value: 'pending' },
        { label: 'On Hold', value: 'onHold' },
        {
          label: 'Liquidation request sent to RK',
          value: 'liquidationRequested'
        },
        { label: 'Liquidated', value: 'liquidated' },
        { label: 'Stalled', value: 'stalled' },
        { label: 'Canceled', value: 'canceled' },
        { label: 'Unknown', value: 'unknown' }
      ],
      type: 'multiSelect'
    },
    {
      column: 'responseDeadline',
      endName: 'endResponseDeadline',
      label: 'Response Deadline',
      startName: 'startResponseDeadline',
      type: 'dateRange'
    },
    {
      column: 'liquidationDate',
      endName: 'endLiquidationDate',
      label: 'Liquidated',
      startName: 'startLiquidationDate',
      type: 'dateRange'
    }
  ];

  return (
    <Stack sx={{ flex: 1, minHeight: 0 }}>
      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        filterDefs={filterDefs}
        getRowId={row => row.rolloverParticipantId}
        initialSort={[{ field: 'createdAt', sort: 'desc' }]}
        loading={!query || candidates.isFetching}
        onClear={setQuery}
        onSearch={setQuery}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationMode='server'
        pinnedColumns={{ left: ['participant'], right: ['actions'] }}
        rowCount={candidates.data?.pagination?.total ?? 0}
        rows={candidates.data?.data ?? []}
        showColumnMenu
        showToolbar
        slotProps={{
          noRowsOverlay: {
            message: 'No force out candidates match this search'
          },
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'autoForceOuts'
          },
          toolbar: {
            onDraftChange: onDraftOrgChange,
            onRefresh: candidates.refetch,
            refreshTestId: 'autoForceOutsRefresh',
            searchButtonTestId: 'autoForceOutsSearch',
            triggerTestId: 'autoForceOutsFilterTrigger'
          }
        }}
        slots={{ toolbar: FilterToolbar }}
        sortingMode='server'
        sx={{ flex: 1, minHeight: 0 }}
      />
    </Stack>
  );
};

export const Route = createFileRoute('/auto-force-outs/participants')({
  component: Page
});
